import type { ReactNode } from "react";

import { Badge } from "@/components/ui/Badge";
import { HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

export const TLA_PLANNING_NUM_CELL_CLASS = "px-3 py-2 text-right tabular-nums";

export interface TlaPlanningSupplyDetailRow {
  build_order_id?: number | null;
  count_as_assigned?: boolean;
  is_shadow_reconciliation?: boolean;
  mv_number: string | null;
  work_order_number: string | null;
  purchase_order: string;
  work_order_quantity: number;
  ordered: number;
  allocated: number;
  remaining: number;
  submitted_date: string | null;
  earliest_assembled: string | null;
  latest_assembled: string | null;
}

export interface TlaPlanningQtyOrderedDetailRow {
  purchase_order: string;
  work_order_number: string | null;
  ordered_quantity: number;
  received_quantity: number;
  scanned_quantity: number;
  qty_ordered: number;
  submitted_date: string | null;
  is_shadow_reconciliation?: boolean;
}

export function TlaPlanningPoWithShadowLabel({
  purchaseOrder,
  isShadowReconciliation,
}: {
  purchaseOrder: string;
  isShadowReconciliation?: boolean;
}): ReactNode {
  const label = purchaseOrder.trim() === "" ? "—" : purchaseOrder;
  if (!isShadowReconciliation) {
    return label;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span>{label}</span>
      <Badge tone="warning" className="px-1 py-0 text-[10px] font-medium uppercase tracking-wide">
        Shadow
      </Badge>
    </span>
  );
}

export interface TlaPlanningDemandDetailRow {
  batch_id: number | null;
  sales_order_number: string;
  finished_sku: string;
  work_order_number: string | null;
  batch_quantity: number;
  bom_quantity: number;
  tla_needed: number;
  fulfilled: number;
  pending: number;
  sgs_ship_by_date: string | null;
  source: "batch" | "unassigned";
}

export function formatTlaPlanningDate(value: string | null): string {
  if (value == null || value.trim() === "") return "—";
  return value.slice(0, 10);
}

export function TlaPlanningTableHeaderWithHelp({
  label,
  hoverHint,
  className,
  align = "left",
  labelClassName,
}: {
  label: string;
  hoverHint: string;
  className?: string;
  align?: "left" | "right";
  labelClassName?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "align-bottom font-medium text-slate-500",
        className,
        align === "right" ? "text-right" : "text-left",
      )}
    >
      <HoverHintContent hoverHint={hoverHint} align="start" block>
        <span
          className={cn(
            "inline-block leading-tight",
            labelClassName ?? "max-w-[12rem] leading-tight",
            align === "right" ? "ml-auto text-right" : undefined,
          )}
        >
          {label}
        </span>
      </HoverHintContent>
    </th>
  );
}

export function TlaPlanningSortableTableHeader({
  label,
  hoverHint,
  sortKey,
  activeSortKey,
  sortDir,
  onSort,
  className,
  align = "left",
}: {
  label: ReactNode;
  hoverHint: string;
  sortKey: string;
  activeSortKey: string;
  sortDir: "asc" | "desc";
  onSort: (key: string) => void;
  className?: string;
  align?: "left" | "right";
}) {
  const active = activeSortKey === sortKey;
  return (
    <th
      scope="col"
      className={cn(
        "cursor-pointer select-none align-bottom font-medium text-slate-500 hover:bg-slate-50",
        className,
        align === "right" ? "text-right" : "text-left",
      )}
      onClick={() => onSort(sortKey)}
    >
      <HoverHintContent hoverHint={hoverHint} align="start" block>
        <span
          className={cn(
            "inline-block leading-tight",
            align === "right" ? "ml-auto text-right" : undefined,
          )}
        >
          {label}
          {active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
        </span>
      </HoverHintContent>
    </th>
  );
}
